import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Clock4 } from "lucide-react";
import DateRangeFilter, { filterAppointmentsByDate } from "@/components/DateRangeFilter";

const STATUS = {
  confirmed: { text: "Confirmada", cls: "bg-secondary text-secondary-foreground" },
  completed: { text: "Completada", cls: "bg-slate-200 text-slate-700" },
  cancelled: { text: "Cancelada", cls: "bg-red-50 text-red-700" },
};

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [appts, setAppts] = useState([]);
  const [dateFilter, setDateFilter] = useState("next5");

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        api.get("/staff/me/profile"),
        api.get("/staff/me/appointments"),
      ]);
      setProfile(p.data);
      setAppts(a.data);
    } catch (err) {
      console.error("StaffDashboard load failed:", err?.message || err);
      toast.error("No pudimos cargar tu agenda");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function changeStatus(id, status) {
    try {
      await api.put(`/staff/me/appointments/${id}/status`, { status });
      toast.success("Estado actualizado");
      load();
    } catch { toast.error("Error"); }
  }

  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const filteredAppts = filterAppointmentsByDate(appts, dateFilter);
  const upcoming = filteredAppts.filter((a) => a.status === "confirmed");
  const todayAppts = upcoming.filter((a) => a.date === today);
  const futureAppts = upcoming.filter((a) => a.date > today);
  const past = filteredAppts.filter((a) => a.status !== "confirmed");

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-slate-200 glass-nav sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="staff-logo">
            <BrandLogo />
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline">{user?.name}</span>
            <Link to="/staff/reports"><Button variant="outline" data-testid="staff-reports-link">Mi reporte</Button></Link>
            <Button variant="outline" onClick={() => logout().then(() => navigate("/"))} data-testid="staff-logout-btn">Cerrar sesión</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="overline">Mi agenda</p>
            <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-3">Hola, {profile?.name}</h1>
            <p className="text-slate-600 mt-1">{profile?.title}</p>
          </div>
          <DateRangeFilter value={dateFilter} onChange={setDateFilter} testidPrefix="staff-range" />
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-10" data-testid="staff-stats">
          <Card className="border-slate-200"><CardContent className="p-6">
            <div className="flex items-center justify-between"><p className="overline">Hoy</p><CalendarDays className="h-4 w-4 text-slate-400" /></div>
            <p className="font-display text-4xl font-light mt-3">{todayAppts.length}</p>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-6">
            <div className="flex items-center justify-between"><p className="overline">Próximas</p><Clock4 className="h-4 w-4 text-slate-400" /></div>
            <p className="font-display text-4xl font-light mt-3">{futureAppts.length}</p>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-6">
            <div className="flex items-center justify-between"><p className="overline">Completadas</p><CheckCircle2 className="h-4 w-4 text-slate-400" /></div>
            <p className="font-display text-4xl font-light mt-3">{past.filter((a) => a.status === "completed").length}</p>
          </CardContent></Card>
        </div>

        <section className="mb-12">
          <h2 className="font-display text-xl mb-4">Próximas citas</h2>
          {upcoming.length === 0 ? (
            <Card className="border-slate-200"><CardContent className="p-8 text-slate-500" data-testid="staff-no-upcoming">No tienes citas próximas.</CardContent></Card>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <Table data-testid="staff-upcoming-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Hora</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Servicio</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {upcoming.map((a) => (
                      <TableRow key={a.id} data-testid={`staff-appt-${a.id}`}>
                        <TableCell className="font-medium">{a.date}</TableCell>
                        <TableCell>{a.time}</TableCell>
                        <TableCell>
                          <div className="font-medium">{a.client_name}</div>
                          <div className="text-xs text-slate-500">{a.client_email}</div>
                        </TableCell>
                        <TableCell>{a.service_name}</TableCell>
                        <TableCell><Badge className={STATUS[a.status].cls}>{STATUS[a.status].text}</Badge></TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button size="sm" variant="outline" onClick={() => changeStatus(a.id, "completed")} data-testid={`staff-complete-${a.id}`}>Completar</Button>
                          <Button size="sm" variant="ghost" onClick={() => changeStatus(a.id, "cancelled")} data-testid={`staff-cancel-${a.id}`}>Cancelar</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </section>

        <section>
          <h2 className="font-display text-xl mb-4">Historial</h2>
          {past.length === 0 ? (
            <Card className="border-slate-200"><CardContent className="p-8 text-slate-500">Sin historial todavía.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {past.slice(0, 30).map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-5 py-4" data-testid={`staff-past-${a.id}`}>
                  <div>
                    <p className="font-display">{a.service_name} · <span className="text-slate-500 text-sm">{a.client_name}</span></p>
                    <p className="text-xs text-slate-500">{a.date} · {a.time}</p>
                  </div>
                  <Badge className={STATUS[a.status].cls}>{STATUS[a.status].text}</Badge>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
